import { OTHER_PAYMENT_OPTION_ID } from '@webapp/components/RequestableCreditCardSelect'
import { computeExpenseLineItemTotalsUseCase } from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import { isCustomTax } from '@webapp/modules/expense/components/ExpenseTaxSelect'
import {
  ExpensePaymentTypes,
  type ExpensePaymentTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { ExpenseTypes } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { type ExpenseLineItemFormSchema } from './ExpenseLineItemForm'
import { type ExpenseItemFormSchema } from '../ExpenseLineItemDrawer/types'

const buildExpenseTypeData = (
  values: ExpenseLineItemFormSchema
): ExpenseItemFormSchema['expenseTypeData'] => {
  switch (values.expenseType) {
    case ExpenseTypes.MEALS:
      return { type: 'MEALS', data: { tips: values.tips } }
    case ExpenseTypes.MILEAGE:
      return {
        type: 'MILEAGE',
        data: {
          mileageRate: values.mileageRate,
          distance: values.distance,
          unitOfDistance: values.unitOfDistanceStr.value as 'km' | 'mi',
        },
      }
    case ExpenseTypes.LABOUR:
      return {
        type: 'LABOUR',
        data: {
          hourlyRate: values.hourlyRate,
          hoursOfWork: values.hoursOfWork,
        },
      }
    case ExpenseTypes.AIRFARE:
      return {
        type: 'AIRFARE',
        data: {
          airline: values.airline,
          ticketNumber: values.ticketNumber,
          departureDate: values.departureDate
            ? values.departureDate.format('YYYY-MM-DD')
            : null,
          arrivalDate: values.arrivalDate
            ? values.arrivalDate.format('YYYY-MM-DD')
            : null,
        },
      }
    case ExpenseTypes.LODGING:
      return { type: 'LODGING', data: null }
    default:
      return { type: 'OTHER', data: null }
  }
}

export const mapFormSchemaToExpenseItemFormSchema = (
  values: ExpenseLineItemFormSchema,
  item: ExpenseItem | undefined
): ExpenseItemFormSchema => {
  if (values.currency == null) {
    // Yup validation requires currency before submit, so this path only
    // fires when validation has been bypassed.
    throw new Error('ExpenseLineItemForm submitted without a currency')
  }

  const taxTypeValue = values.taxType
  const isCustom = isCustomTax(taxTypeValue)

  const { computedSubtotal, computedTaxAmount, computedTotal } =
    computeExpenseLineItemTotalsUseCase.execute({
      expenseType: values.expenseType,
      subtotal: values.subtotal,
      tax: isCustom
        ? { mode: 'custom', percentage: taxTypeValue.percentage }
        : {
            mode: taxTypeValue.id === '$' ? 'dollar' : 'percent',
            amount: values.taxAmount,
          },
      tips: values.tips,
      mileageRate: values.mileageRate,
      distance: values.distance,
      hourlyRate: values.hourlyRate,
      hoursOfWork: values.hoursOfWork,
    })

  const taxMode: ExpenseItemFormSchema['taxMode'] = isCustom
    ? 'saved'
    : taxTypeValue.id === '%'
      ? 'percent'
      : 'dollar'

  const taxName = isCustom ? taxTypeValue.name : taxTypeValue.id
  const taxPercentage = isCustom
    ? taxTypeValue.percentage
    : taxTypeValue.id === '%'
      ? String(values.taxAmount / 100)
      : '0'
  const taxId = isCustom ? taxTypeValue.id : null

  // Reimbursable items do not collect a payment method from the UI, so any
  // stale selection is cleared and the original paymentType is preserved on
  // edit (OTHER for new items). For non-reimbursable items with no selection
  // (e.g. CASH/CHEQUE on edit, which have no matching Select option), preserve
  // the item's original paymentType rather than silently coercing to OTHER.
  const resolvedPaymentMethod = values.reimbursable
    ? null
    : (values.paymentMethod ?? null)

  const resolvedPaymentType: ExpensePaymentTypeEnum = (() => {
    if (resolvedPaymentMethod == null) {
      return item?.paymentType ?? ExpensePaymentTypes.OTHER
    }
    if (resolvedPaymentMethod.id === OTHER_PAYMENT_OPTION_ID) {
      return ExpensePaymentTypes.OTHER
    }
    return ExpensePaymentTypes.CREDIT_CARD_ASSIGNED
  })()

  return {
    id: item?.id ?? null,
    merchant: values.merchant,
    account: values.account ?? null,
    currency: values.currency,
    reimbursable: values.reimbursable,
    expenseType: values.expenseType,
    paymentType: resolvedPaymentType,
    paymentMethod: resolvedPaymentMethod,
    amount: String(computedTotal),
    subtotal: String(computedSubtotal),
    taxMode,
    taxValue: String(values.taxAmount),
    taxAmount: String(computedTaxAmount),
    taxId,
    taxName,
    taxPercentage,
    taxIsInclusive: isCustom ? taxTypeValue.isInclusive : false,
    transactionDate: values.transDate
      ? values.transDate.format('YYYY-MM-DD')
      : '',
    attachment: values.attachment,
    notes: values.notes,
    expenseTypeData: buildExpenseTypeData(values),
    mileageRatePreset:
      values.expenseType === ExpenseTypes.MILEAGE
        ? values.mileageRatePreset
        : null,
  }
}
